
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { Upload } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { AnimatedProgressProvider } from "@/components/ui/animated-progress-provider";
import { easeQuadInOut } from "d3-ease";
import { toast } from "sonner";
import "react-circular-progressbar/dist/styles.css";
import { getAvatarUrl } from "@/utils/storageUtils";
import { useState, useEffect } from "react";

type Profile = Tables<"profiles">;

interface ProfileAvatarProps {
  profile: Profile;
  remainingVacationDays: number;
  isOwnProfile: boolean;
  onAvatarUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ProfileAvatar = ({ 
  profile, 
  remainingVacationDays, 
  isOwnProfile, 
  onAvatarUpload 
}: ProfileAvatarProps) => {
  const [imageError, setImageError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  
  // Get initials from full name
  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ')
      .filter(n => n.length > 0)
      .map(n => n[0].toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Calculate progress percentage
  const totalVacationDays = profile?.vacation_days || 20;
  const progressPercentage = (remainingVacationDays / totalVacationDays) * 100;

  useEffect(() => {
    if (profile?.avatar_url) {
      try {
        const url = getAvatarUrl(profile.avatar_url);
        setAvatarUrl(url);
        setImageError(false);
      } catch (error) {
        console.error('Error getting avatar URL:', error);
        setImageError(true);
      }
    }
  }, [profile?.avatar_url]);

  const handleAvatarError = () => {
    console.error('Failed to load avatar image in ProfileAvatar');
    setImageError(true);
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      toast.error('No file selected');
      return;
    }
    
    const maxSizeInMB = 5;
    if (file.size > maxSizeInMB * 1024 * 1024) {
      toast.error(`File size exceeds ${maxSizeInMB}MB limit`);
      return;
    }

    setImageError(false); // Reset error state on new upload
    setIsUploading(true);
    
    try {
      onAvatarUpload(event);
    } catch (error) {
      console.error('Error during avatar upload:', error);
      toast.error('Failed to upload avatar');
    } finally {
      // Set uploading to false after a delay to allow for UI feedback
      setTimeout(() => setIsUploading(false), 3000);
    }
  };

  return (
    <div className="relative w-64 h-64">
      <div className="absolute inset-0 rounded-full p-2" style={{ 
        background: 'linear-gradient(135deg, #D3E4FD 0%, #E5DEFF 50%, #F1F0FB 100%)',
        boxShadow: '0 0 0 2px rgba(255,255,255,0.8)'
      }}>
        <Avatar className="h-full w-full border-2 border-white/30">
          {avatarUrl && !imageError ? (
            <AvatarImage 
              src={avatarUrl}
              className="object-cover"
              alt={profile?.full_name || 'User avatar'}
              onError={handleAvatarError}
            />
          ) : (
            <AvatarFallback className="text-4xl font-semibold bg-transparent text-gray-700">
              {getInitials(profile?.full_name)}
            </AvatarFallback>
          )}
        </Avatar>
      </div>
      {isOwnProfile && (
        <label 
          htmlFor="avatar-upload" 
          className={`absolute top-[0.9rem] left-[0.9rem] p-3 ${isUploading ? 'bg-gray-400' : 'bg-primary'} text-white rounded-full cursor-pointer hover:bg-primary/90 shadow-lg transition-colors`}
        >
          <Upload className={`h-6 w-6 ${isUploading ? 'animate-pulse' : ''}`} />
        </label>
      )}
      <div className="absolute -bottom-4 -right-4 w-28 h-28">
        <div className="relative">
          <AnimatedProgressProvider
            valueStart={0}
            valueEnd={progressPercentage}
            duration={1.4}
            easingFunction={easeQuadInOut}
            repeat
          >
            {value => (
              <CircularProgressbar
                value={value}
                text={`${remainingVacationDays}`}
                background
                backgroundPadding={6}
                styles={buildStyles({
                  backgroundColor: "#ffffff",
                  textColor: "#000000",
                  textSize: '28px',
                  pathColor: remainingVacationDays > 5 ? "#4ec500" : "#ff4444",
                  trailColor: "rgba(200, 200, 200, 0.3)",
                  pathTransition: "none",
                  strokeLinecap: 'round'
                })}
                className="font-bold"
              />
            )}
          </AnimatedProgressProvider>
          <div className="absolute bottom-[1.85rem] left-1/2 transform -translate-x-1/2 text-xs text-gray-600">
            days left
          </div>
        </div>
      </div>
      {isOwnProfile && (
        <input
          id="avatar-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarChange}
          disabled={isUploading}
        />
      )}
    </div>
  );
};
